import {
    FC,
    PropsWithChildren,
    ReactElement,
    useEffect,
} from 'react';

import { useLocation, useSearchParams } from 'react-router-dom';

import { PaymentStatus, stateParamKey } from '../../../entities/@api/Sylius';
import { scrollToTop } from '../../../helpers/scroll';
import { useClientEffect, useTimeout } from '../../../hooks';
import { storeActiveCart } from '../../../redux/@account/activeCart/activeCartActions';
import { fetchCustomer } from '../../../redux/@account/customer/customerActions';
import { fetchCustomPageSlugs } from '../../../redux/@pages/customPages/customPagesActions';
import { fetchProductCategorySlugs } from '../../../redux/@products/productCategories/productCategoriesActions';
import { setAllowScrollReset } from '../../../redux/app/appSlice';
import { useTypedDispatch, useTypedSelector } from '../../../redux/store';

export const ConnectedAppWrapper: FC<PropsWithChildren> = ({
    children,
}): ReactElement => {
    const { pathname, hash } = useLocation();
    const [searchParams] = useSearchParams();

    const dispatch = useTypedDispatch();

    const allowScrollReset = useTypedSelector(state => state.appSlice.allowScrollReset);

    const customer = useTypedSelector(state => state.customerSlice.customer);

    const cart = useTypedSelector(state => state.cartSlice.cart);

    const hasFetchedActiveCart = useTypedSelector(state => state.activeCartSlice.hasFetched);
    const activeCartToken = useTypedSelector(state => state.activeCartSlice.activeCartToken);

    useClientEffect((): void => {
        dispatch(fetchCustomPageSlugs());
        dispatch(fetchProductCategorySlugs());
    }, []);

    useEffect((): void => {
        const orderState = searchParams.get(stateParamKey);
        const isThankYouPage = orderState === PaymentStatus.success || orderState === PaymentStatus.pending;

        dispatch(fetchCustomer({ fetchCartAndWishlist: !isThankYouPage }));
    }, []);

    useEffect((): void => {
        const canStore = hasFetchedActiveCart && customer && cart;

        if (canStore && !activeCartToken) {
            dispatch(storeActiveCart(customer.id, cart.id));
        }
    }, [hasFetchedActiveCart, customer, activeCartToken, cart]);

    useEffect((): void => {
        if (allowScrollReset && !hash) {
            scrollToTop('auto');
        }
    }, [pathname]);

    // Reset scroll lock boolean
    useTimeout((): void => {
        if (!allowScrollReset) {
            dispatch(setAllowScrollReset(true));
        }
    }, 10, [allowScrollReset]);

    return (
        <div>
            {children}
        </div>
    );
};
